const DATE_ONLY_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/;
const SCHEDULED_WALL_PATTERN =
  /^(\d{4})-(\d{2})-(\d{2})[T\s](\d{2}):(\d{2})(?::(\d{2})(?:\.(\d{1,3}))?)?/;

const pad = (value: number, length = 2) => String(value).padStart(length, "0");

export function isValidTimeZone(timeZone: unknown): timeZone is string {
  if (typeof timeZone !== "string" || !timeZone.trim()) return false;

  try {
    new Intl.DateTimeFormat("en-US", { timeZone }).format();
    return true;
  } catch {
    return false;
  }
}

function assertValidTimeZone(timeZone: string) {
  if (!isValidTimeZone(timeZone)) {
    throw new Error(`Invalid salon timezone: ${timeZone || "missing"}`);
  }
}

function datePartsInTimeZone(instant: Date, timeZone: string) {
  assertValidTimeZone(timeZone);
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone,
    calendar: "gregory",
    numberingSystem: "latn",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hourCycle: "h23",
  }).formatToParts(instant);
  const values = Object.fromEntries(parts.map((part) => [part.type, part.value]));

  return {
    year: Number(values.year),
    month: Number(values.month),
    day: Number(values.day),
    hour: Number(values.hour),
    minute: Number(values.minute),
    second: Number(values.second),
  };
}

function isValidUtcDate(
  date: Date,
  year: number,
  month: number,
  day: number,
  hour = 0,
  minute = 0,
  second = 0,
) {
  return (
    date.getUTCFullYear() === year &&
    date.getUTCMonth() === month - 1 &&
    date.getUTCDate() === day &&
    date.getUTCHours() === hour &&
    date.getUTCMinutes() === minute &&
    date.getUTCSeconds() === second
  );
}

// Date-only values are civil calendar dates and never represent instants.
export function parseDateOnly(value: string): Date | null {
  const match = DATE_ONLY_PATTERN.exec(value);
  if (!match) return null;

  const [, year, month, day] = match.map(Number);
  const date = new Date(Date.UTC(year, month - 1, day));
  return isValidUtcDate(date, year, month, day) ? date : null;
}

export function formatDateOnly(date: Date): string {
  return `${date.getUTCFullYear()}-${pad(date.getUTCMonth() + 1)}-${pad(
    date.getUTCDate(),
  )}`;
}

export function dateOnlyFromInstant(
  instant: Date,
  timeZone: string,
): string {
  const parts = datePartsInTimeZone(instant, timeZone);
  return `${parts.year}-${pad(parts.month)}-${pad(parts.day)}`;
}

export function addDaysToDateOnly(date: Date, days: number): Date {
  const result = new Date(date.getTime());
  result.setUTCDate(result.getUTCDate() + days);
  return result;
}

export function differenceInDateOnlyDays(later: Date, earlier: Date): number {
  const laterDay = Date.UTC(
    later.getUTCFullYear(),
    later.getUTCMonth(),
    later.getUTCDate(),
  );
  const earlierDay = Date.UTC(
    earlier.getUTCFullYear(),
    earlier.getUTCMonth(),
    earlier.getUTCDate(),
  );
  return Math.round((laterDay - earlierDay) / 86_400_000);
}

export function weekdayForDateOnly(date: Date, format: "long" | "short" = "long") {
  return new Intl.DateTimeFormat("en-US", {
    timeZone: "UTC",
    weekday: format,
  }).format(date);
}

export function formatDateOnlyForDisplay(
  date: Date,
  options: Intl.DateTimeFormatOptions,
  locale = "en-US",
) {
  return new Intl.DateTimeFormat(locale, { ...options, timeZone: "UTC" }).format(date);
}

// Scheduled values store salon wall-clock components in UTC-backed timestamps.
export function parseScheduledWall(value: string): Date | null {
  const match = SCHEDULED_WALL_PATTERN.exec(value);
  if (!match) return null;

  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const hour = Number(match[4]);
  const minute = Number(match[5]);
  const second = Number(match[6] || 0);
  const millisecond = Number((match[7] || "0").padEnd(3, "0"));
  const date = new Date(
    Date.UTC(year, month - 1, day, hour, minute, second, millisecond),
  );

  return isValidUtcDate(date, year, month, day, hour, minute, second)
    ? date
    : null;
}

export function encodeScheduledWall(dateOnly: string, time: string): string {
  const date = parseDateOnly(dateOnly);
  const match = /^(\d{2}):(\d{2})$/.exec(time);
  if (!date || !match) throw new Error("Invalid scheduled salon date or time");

  const hour = Number(match[1]);
  const minute = Number(match[2]);
  if (hour > 23 || minute > 59) {
    throw new Error("Invalid scheduled salon date or time");
  }

  date.setUTCHours(hour, minute, 0, 0);
  return date.toISOString();
}

export function addMinutesToScheduledWall(value: string, minutes: number): string {
  const date = parseScheduledWall(value);
  if (!date) throw new Error("Invalid scheduled salon timestamp");

  date.setUTCMinutes(date.getUTCMinutes() + minutes);
  return date.toISOString();
}

export function formatScheduledWall(
  value: string,
  options: Intl.DateTimeFormatOptions,
  locale = "en-US",
): string {
  const date = parseScheduledWall(value);
  if (!date) return "";

  return new Intl.DateTimeFormat(locale, { ...options, timeZone: "UTC" }).format(date);
}

export function formatScheduledDate(value: string): string {
  return formatScheduledWall(value, {
    weekday: "long",
    month: "long",
    day: "numeric",
    year: "numeric",
  });
}

export function formatScheduledTime(value: string): string {
  return formatScheduledWall(value, {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
}

export function formatScheduledWallForCalendar(value: string): string {
  const date = parseScheduledWall(value);
  if (!date) return "";

  return `${date.getUTCFullYear()}${pad(date.getUTCMonth() + 1)}${pad(
    date.getUTCDate(),
  )}T${pad(date.getUTCHours())}${pad(date.getUTCMinutes())}${pad(
    date.getUTCSeconds(),
  )}`;
}

// Real instants identify a single point on the global timeline.
export function formatRealInstantForCalendar(instant: Date): string {
  return instant.toISOString().replace(/[-:]/g, "").replace(/\.\d{3}Z$/, "Z");
}

export function realInstantAsSalonWallEpoch(
  instant: Date,
  timeZone: string,
): number {
  const parts = datePartsInTimeZone(instant, timeZone);
  return Date.UTC(
    parts.year,
    parts.month - 1,
    parts.day,
    parts.hour,
    parts.minute,
    parts.second,
    instant.getUTCMilliseconds(),
  );
}

export function scheduledWallToRealInstant(
  value: string,
  timeZone: string,
): Date {
  assertValidTimeZone(timeZone);
  const wallDate = parseScheduledWall(value);
  if (!wallDate) throw new Error("Invalid scheduled salon timestamp");

  const wallEpoch = wallDate.getTime();
  const offsets = new Set<number>();

  // Sample both sides of nearby offset transitions, then verify candidates by
  // formatting them back into the requested salon wall time.
  for (let hours = -48; hours <= 48; hours += 6) {
    const probe = new Date(wallEpoch + hours * 3_600_000);
    offsets.add(realInstantAsSalonWallEpoch(probe, timeZone) - probe.getTime());
  }

  const matches = [...offsets]
    .map((offset) => new Date(wallEpoch - offset))
    .filter(
      (candidate) =>
        realInstantAsSalonWallEpoch(candidate, timeZone) === wallEpoch,
    )
    .sort((a, b) => a.getTime() - b.getTime());

  if (matches.length === 0) {
    throw new Error("Scheduled salon time does not exist in its timezone");
  }

  // Match common calendar/Temporal-compatible behavior for repeated wall times.
  return matches[0];
}

export function scheduledWallRangeToRealInstants(
  start: string,
  end: string,
  timeZone: string,
) {
  const startInstant = scheduledWallToRealInstant(start, timeZone);
  const endInstant = scheduledWallToRealInstant(end, timeZone);
  if (endInstant <= startInstant) {
    throw new Error("Scheduled salon end time must be after its start time");
  }

  return { startInstant, endInstant };
}

export function nextSalonDateBoundary(
  timeZone: string,
  after = new Date(),
): Date {
  assertValidTimeZone(timeZone);
  const currentDate = dateOnlyFromInstant(after, timeZone);
  let lower = after.getTime();
  let upper = lower + 36 * 3_600_000;

  while (
    dateOnlyFromInstant(new Date(upper), timeZone) === currentDate &&
    upper - lower < 72 * 3_600_000
  ) {
    upper += 12 * 3_600_000;
  }

  if (dateOnlyFromInstant(new Date(upper), timeZone) === currentDate) {
    throw new Error("Unable to determine the next salon date boundary");
  }

  while (upper - lower > 1) {
    const midpoint = Math.floor((lower + upper) / 2);
    if (dateOnlyFromInstant(new Date(midpoint), timeZone) === currentDate) {
      lower = midpoint;
    } else {
      upper = midpoint;
    }
  }

  return new Date(upper);
}

export function isRealInstantWithinScheduledWallRange(
  instant: Date,
  start: string,
  end: string,
  timeZone: string,
): boolean {
  try {
    const { startInstant, endInstant } = scheduledWallRangeToRealInstants(
      start,
      end,
      timeZone,
    );
    return instant >= startInstant && instant <= endInstant;
  } catch {
    return false;
  }
}

export function salonTimeZoneLabel(timeZone: string, instant = new Date()): string {
  const label = new Intl.DateTimeFormat("en-US", {
    timeZone,
    timeZoneName: "long",
  })
    .formatToParts(instant)
    .find((part) => part.type === "timeZoneName")?.value;
  const offset = new Intl.DateTimeFormat("en-US", {
    timeZone,
    timeZoneName: "shortOffset",
  })
    .formatToParts(instant)
    .find((part) => part.type === "timeZoneName")?.value;

  if (!label) return timeZone;
  return offset && offset !== label ? `${label} (${offset})` : label;
}

export function escapeIcsText(value: string): string {
  return value
    .replace(/\\/g, "\\\\")
    .replace(/\r?\n/g, "\\n")
    .replace(/;/g, "\\;")
    .replace(/,/g, "\\,");
}
